import React from 'react';
import { motion } from 'framer-motion';
import { Leaf, Sun, Package, FlaskConical, Activity, Zap, BookOpen, Clock, ShieldCheck } from 'lucide-react';

/**
 * Product Specifications & Key Benefits — a standardised compliance block
 * surfaced on the Home page product feature and the BCM-95® detail
 * page. Copy is locked to UK MHRA/ASA-compliant phrasing (no medical
 * claims) and matches the printed label on every bottle.
 */
export default function ProductSpecsBlock({ className = '' }) {
  return (
    <motion.section
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.6 }}
      className={`rounded-3xl border border-emerald-700/15 bg-gradient-to-br from-emerald-50/70 via-white to-amber-50/40 p-6 md:p-8 shadow-sm ${className}`}
      data-testid="product-specs-block"
    >
      <div className="flex items-center gap-2 mb-5">
        <FlaskConical className="w-5 h-5 text-emerald-700" />
        <h3 className="text-xl md:text-2xl font-bold font-heading text-emerald-900">
          Product Specifications &amp; Key Benefits
        </h3>
      </div>

      {/* Everyday Support — customer-benefit cards lead the technical panel */}
      <div className="mb-5" data-testid="product-benefit-cards">
        <h4 className="text-lg md:text-xl font-bold font-heading text-emerald-900 mb-1">
          Everyday Support for Life in Motion
        </h4>
        <p className="text-[13px] text-slate-600 mb-4 max-w-2xl">
          Built on the clinically researched BCM-95® extract, BCM-95® is a simple daily supplement for people who want to stay active and keep doing the things they enjoy.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {[
            { Icon: Activity, title: 'Made for an Active Life', sub: 'For people who want to keep moving and keep doing what they love.' },
            { Icon: Zap, title: 'High-Bioavailability Curcumin', sub: 'BCM-95® is absorbed better than standard curcumin in published pharmacokinetic research.' },
            { Icon: BookOpen, title: 'Backed by Extensive Research', sub: 'One of the most extensively studied curcumin formulations available today.' },
            { Icon: Leaf, title: 'Single-Plant & Piperine-Free', sub: '100% turmeric extract with its own natural oils — no black-pepper enhancer.' },
            { Icon: Clock, title: 'A Simple Daily Routine', sub: 'Just two vegetarian capsules a day, taken with food.' },
            { Icon: ShieldCheck, title: 'Made to GMP Standards', sub: 'Produced under GMP-certified manufacturing.' },
          ].map(({ Icon, title, sub }) => (
            <div key={title} className="rounded-xl bg-white border border-emerald-700/10 p-4 flex items-start gap-3">
              <div className="w-9 h-9 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center flex-shrink-0">
                <Icon className="w-4.5 h-4.5" strokeWidth={2.2} />
              </div>
              <div>
                <div className="font-bold text-sm text-emerald-900 mb-0.5 leading-tight">{title}</div>
                <p className="text-[12px] text-slate-600 leading-snug">{sub}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Typical Values — locked formulation panel */}
      <div className="rounded-2xl bg-white border border-emerald-700/10 p-5 mb-5" data-testid="product-specs-typical-values">
        <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-emerald-700/80 mb-3">
          Typical Values · per 2 capsules
        </div>
        <div className="grid sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
          <div className="flex items-baseline justify-between border-b border-dashed border-emerald-700/10 pb-2">
            <span className="text-slate-700">
              BCM-95<sup className="text-[8px]">®</sup><br />
              <span className="text-[11px] text-muted-foreground">Purified Turmeric Extract · Curcugreen<sup className="text-[8px]">®</sup></span>
            </span>
            <span className="font-mono font-bold text-emerald-900 whitespace-nowrap">1000 mg</span>
          </div>
          <div className="flex items-baseline justify-between border-b border-dashed border-emerald-700/10 pb-2">
            <span className="text-slate-700">
              Of which Curcumin<br />
              <span className="text-[11px] text-muted-foreground">High-bioavailable curcuminoids</span>
            </span>
            <span className="font-mono font-bold text-emerald-900 whitespace-nowrap">650 mg</span>
          </div>
        </div>
      </div>

      {/* Benefit chips */}
      <div className="grid sm:grid-cols-3 gap-3" data-testid="product-specs-benefits">
        <div className="rounded-xl bg-white border border-emerald-700/10 p-4 flex items-start gap-3" data-testid="product-specs-allergen">
          <div className="w-9 h-9 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center flex-shrink-0">
            <Leaf className="w-4.5 h-4.5" strokeWidth={2.2} />
          </div>
          <div>
            <div className="font-bold text-sm text-emerald-900 mb-0.5">Allergen-Free</div>
            <p className="text-[12px] text-slate-600 leading-snug">
              Absolutely no allergens in this product — including the vegetable capsules.
            </p>
          </div>
        </div>

        <div className="rounded-xl bg-white border border-emerald-700/10 p-4 flex items-start gap-3" data-testid="product-specs-solar">
          <div className="w-9 h-9 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center flex-shrink-0">
            <Sun className="w-4.5 h-4.5" strokeWidth={2.2} />
          </div>
          <div>
            <div className="font-bold text-sm text-emerald-900 mb-0.5">Eco-Friendly Production</div>
            <p className="text-[12px] text-slate-600 leading-snug">
              Manufactured entirely using solar power to minimise environmental impact.
            </p>
          </div>
        </div>

        <div className="rounded-xl bg-white border border-emerald-700/10 p-4 flex items-start gap-3" data-testid="product-specs-storage">
          <div className="w-9 h-9 rounded-full bg-sky-100 text-sky-700 flex items-center justify-center flex-shrink-0">
            <Package className="w-4.5 h-4.5" strokeWidth={2.2} />
          </div>
          <div>
            <div className="font-bold text-sm text-emerald-900 mb-0.5">Storage Instructions</div>
            <p className="text-[12px] text-slate-600 leading-snug">
              Keep the bottle tightly closed. Store in a cool, dry place between 15°C – 30°C.
            </p>
          </div>
        </div>
      </div>
    </motion.section>
  );
}
